import { BooMark } from "@/components/boo/BooMark";
import { personName } from "@/components/boo/access-copy";
import { DepthMeter } from "@/components/grants/DepthPicker";
import type { BooLinkRow, BooRef, GrantDepth, GrantScopes, Grantable } from "@/lib/api";
import { type ChipOptions, DEPTH_COPY, type LinkState, covers, sameScopes, scopeChips, secretsIn, shortDate } from "@/lib/grant-scopes";
import { cn } from "@/lib/utils";
import { ArrowRight, ShieldAlert } from "lucide-react";

// Small shared pieces of the access screens.

/** The two Boos' marks only, source → the Boo that learns, for where a title already names them. */
export function BooAvatars({ consumer, source, className }: { consumer: BooRef | null; source: BooRef | null; className?: string }) {
  const mark = (boo: BooRef | null) =>
    boo ? <BooMark id={boo.id} className="size-6" muted={!boo.running} /> : <span className="size-6 shrink-0 rounded-full bg-muted" />;
  return (
    <span className={cn("flex items-center gap-1.5", className)} aria-hidden>
      {mark(source)}
      <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" />
      {mark(consumer)}
    </span>
  );
}

/** Which way memories and skills flow, the same everywhere: source → the Boo that learns. */
export function BooPair({ consumer, source, className }: { consumer: BooRef | null; source: BooRef | null; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-x-2.5 gap-y-1", className)}>
      <BooChip boo={source} />
      <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-label="shares with" />
      <BooChip boo={consumer} />
    </div>
  );
}

export function BooChip({ boo, className }: { boo: BooRef | null; className?: string }) {
  return (
    <span className={cn("flex min-w-0 items-center gap-2", className)}>
      {boo ? <BooMark id={boo.id} className="size-6" muted={!boo.running} /> : <span className="size-6 rounded-full bg-muted" />}
      <span className="min-w-0 text-sm font-medium [overflow-wrap:anywhere]">{boo?.name ?? "A deleted Boo"}</span>
    </span>
  );
}

export function DepthTag({ depth, className }: { depth: GrantDepth | null | undefined; className?: string }) {
  if (!depth || depth === "none") return null;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md border border-border px-1.5 py-0.5 text-xs", className)}>
      <DepthMeter depth={depth} />
      {DEPTH_COPY[depth].label}
    </span>
  );
}

export function ScopeChips({
  scopes,
  labels,
  grantable,
  options,
  className,
}: {
  scopes: GrantScopes | null | undefined;
  labels?: Record<string, string>;
  /** The source's grantable, for counts and out of what (shorthand for options.g and options.total). */
  grantable?: Grantable | null;
  options?: ChipOptions;
  className?: string;
}) {
  if (!scopes) return null;
  const chips = scopeChips(scopes, labels, grantable ? { g: grantable, total: grantable, ...options } : (options ?? {}));
  if (!chips.length) return <span className={cn("text-xs text-muted-foreground", className)}>Nothing</span>;
  return (
    <span className={cn("flex flex-wrap gap-1", className)}>
      {chips.map((c) => (
        <span key={c} className="max-w-full rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground [overflow-wrap:anywhere]">
          {c}
        </span>
      ))}
    </span>
  );
}

const STATE_COPY: Record<LinkState, { label: string; tone: "green" | "amber" | "grey" | "red" | "blue" }> = {
  waiting: { label: "Needs approval", tone: "amber" },
  more: { label: "Needs approval", tone: "amber" },
  active: { label: "Learning", tone: "green" },
  partial: { label: "Partly approved", tone: "green" },
  declined: { label: "Declined", tone: "grey" },
  removing: { label: "Stopping", tone: "blue" },
  connecting: { label: "Connecting", tone: "blue" },
  failed: { label: "Couldn't connect", tone: "red" },
  unreachable: { label: "Can't reach", tone: "red" },
};

const TONE: Record<string, string> = {
  green: "bg-emerald-50 text-emerald-800 ring-emerald-600/15 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-400/20",
  amber: "bg-amber-50 text-amber-900 ring-amber-600/20 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-400/20",
  grey: "bg-muted text-muted-foreground ring-border",
  red: "bg-red-50 text-red-800 ring-red-600/15 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-400/20",
  blue: "bg-sky-50 text-sky-800 ring-sky-600/15 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-400/20",
};

/** `side`: whose list it's in. On the source's own list an active grant reads "Sharing"; elsewhere "Learning". */
export function LinkStatePill({ state, side = "learner", className }: { state: LinkState; side?: "learner" | "source"; className?: string }) {
  const s = state === "active" && side === "source" ? { ...STATE_COPY.active, label: "Sharing" } : STATE_COPY[state];
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", TONE[s.tone], className)}>
      <span className={cn("size-1.5 rounded-full bg-current", (state === "connecting" || state === "removing") && "motion-safe:animate-pulse")} aria-hidden />
      {s.label}
    </span>
  );
}

/**
 * "Approved by Jo · Oct 9", or "Jo · Oct 9" under an "Approved by" column
 * (`compact`). A decision an org owner or admin made for the Boo's own
 * admins says so: "Jo (org admin)".
 */
export function DecidedLine({ link, compact }: { link: BooLinkRow; compact?: boolean }) {
  if (!link.decidedAt) return null;
  if (link.decidedVia === "expired") return <span className="text-xs text-muted-foreground">Expired unanswered · {shortDate(link.decidedAt)}</span>;
  const who = link.decidedVia === "rollout" ? "Set up before approvals existed" : `${personName(link.decidedBy, "Someone")}${link.decidedVia === "org_owner" ? " (org admin)" : ""}`;
  const verb = link.status === "declined" ? "Declined" : link.status === "revoked" ? "Ended" : "Approved";
  const text = link.decidedVia === "rollout" ? who : compact && verb === "Approved" ? who : `${verb} by ${who}`;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground [overflow-wrap:anywhere]">
      {link.decidedVia === "org_owner" && <OrgOwnerMark />}
      {text} · {shortDate(link.decidedAt)}
    </span>
  );
}

/** Marks a decision an org owner or admin made for a Boo's own admins. */
export function OrgOwnerMark({ withLabel }: { withLabel?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300" title="Decided by an owner or admin of your organization">
      <ShieldAlert className="size-3.5" aria-hidden />
      {withLabel && <span className="text-xs font-medium">Org admin</span>}
    </span>
  );
}

/**
 * What a grant exposes, said plainly: memories from conversations the other
 * Boo's people weren't in, and the saved secrets its skills run with.
 */
export function ExposureNote({ consumer, source, scopes, grantable }: { consumer: string; source: string; scopes: GrantScopes; grantable?: Grantable }) {
  const ch = "all" in scopes.memories.channels || scopes.memories.channels.only.length > 0;
  const pp = "all" in scopes.memories.people || scopes.memories.people.only.length > 0;
  const secrets = grantable ? secretsIn(scopes, grantable) : { names: [], skills: 0 };
  if (!ch && !pp && !secrets.skills) return null;
  const what = ch && pp ? "in channels and in one-on-one conversations" : ch ? "in these channels" : "in these one-on-one conversations";
  const shown = secrets.names.slice(0, 3).join(", ") + (secrets.names.length > 3 ? ` and ${secrets.names.length - 3} more` : "");
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 [overflow-wrap:anywhere] dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200">
      {(ch || pp) && (
        <p>
          Anyone who can talk to {consumer} will see what {source} learned {what}.
        </p>
      )}
      {secrets.skills > 0 && (
        <p>
          {secrets.skills} {secrets.skills === 1 ? "skill runs" : "skills run"} with saved secrets from {source}: {shown}.
        </p>
      )}
    </div>
  );
}

/**
 * Asked, Approved, In use, one under the other: what the learning Boo asked
 * for, what the source approved, and what it actually uses; one line when
 * all three are the same. `grantable` gives counts: `whole` when it is all
 * the source has (counts read "137 of 171 skills"); otherwise it holds only
 * what was granted (`grantableBound`), a stage it can't account for goes
 * without counts, and only In use counts out of the grant.
 */
export function GrantStages({
  link,
  labels,
  grantable,
  grantableBound,
  whole,
}: {
  link: BooLinkRow;
  labels?: Record<string, string>;
  grantable?: Grantable | null;
  grantableBound?: GrantScopes | null;
  whole?: boolean;
}) {
  const approved = link.status === "approved" && link.granted;
  const using = approved && link.effective && link.setUp;
  const optionsFor = (s: GrantScopes | null | undefined, inUse = false): ChipOptions => {
    if (!grantable || !s || (grantableBound && !covers(grantableBound, s))) return { missing: true };
    return { g: grantable, total: whole || inUse ? grantable : null, missing: true };
  };
  const stage = (title: string, body: React.ReactNode, muted?: boolean) => (
    <div className={cn("grid gap-1.5 sm:grid-cols-[6.5rem_1fr] sm:gap-3", muted && "opacity-60")}>
      <span className="text-xs font-medium text-muted-foreground sm:pt-0.5">{title}</span>
      <div className="min-w-0">{body}</div>
    </div>
  );
  if (using && sameScopes(link.requested, link.granted!) && sameScopes(link.granted!, link.effective!)) {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-xs text-muted-foreground">Everything asked for was approved and is in use.</p>
        <ScopeChips scopes={link.effective} labels={labels} options={optionsFor(link.effective, true)} />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2.5">
      {stage("Asked", <ScopeChips scopes={link.requested} labels={labels} options={optionsFor(link.requested)} />)}
      {stage(
        "Approved",
        approved ? (
          <ScopeChips scopes={link.granted} labels={labels} options={optionsFor(link.granted)} />
        ) : (
          <span className="text-xs text-muted-foreground">{link.status === "declined" ? "Declined" : "Needs approval"}</span>
        ),
        !approved,
      )}
      {stage(
        "In use",
        using ? (
          <ScopeChips scopes={link.effective} labels={labels} options={optionsFor(link.effective, true)} />
        ) : (
          <span className="text-xs text-muted-foreground">{approved ? "Connecting…" : "—"}</span>
        ),
        !using,
      )}
    </div>
  );
}
